import { useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Sidebar } from '@/components/layout/sidebar';
import { PageHeader } from '@/components/layout/page-header';
import { useQuery } from '@tanstack/react-query';
import { useMandateContext } from '@/contexts/mandate-context';
import { getJournalEntries } from '@/services/bookingService';
import { formatSwissCurrency } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const JournalEntries = () => {
  const { selectedMandateId } = useMandateContext();
  const [currentPage, setCurrentPage] = useState(1);
  
  const { data, isLoading } = useQuery({
    queryKey: ['journal-entries', selectedMandateId, currentPage],
    queryFn: () => selectedMandateId ? getJournalEntries(selectedMandateId, currentPage) : null,
    enabled: !!selectedMandateId
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      posted: 'bg-green-100 text-green-800',
      approved: 'bg-blue-100 text-blue-800'
    };
    return variants[status] || variants.draft;
  };

  return (
    <AppLayout>
      <PageHeader
        title="Journal Entries"
        subtitle="Accounting Journal"
      />
      <main className="flex-1 p-page-padding lg:p-page-padding-lg overflow-y-auto">
        <div className="mt-6 bg-white rounded-lg border border-brand-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-brand-secondary border-b border-brand-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-brand-text-secondary">Entry #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-brand-text-secondary">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-brand-text-secondary">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-brand-text-secondary">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-brand-text-secondary">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-brand-text-secondary">Credit</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-brand-text-secondary">Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array(10).fill(0).map((_, i) => (
                    <tr key={i} className="border-b border-brand-border">
                      <td className="px-4 py-3"><Skeleton className="h-4 w-16" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-16" /></td>
                    </tr>
                  ))
                ) : data?.entries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-brand-text-secondary">
                      No journal entries yet. Start by creating bookings from your transactions.
                    </td>
                  </tr>
                ) : (
                  data?.entries.map((entry: any) => {
                    const totalDebit = entry.booking_entries?.reduce(
                      (sum: number, line: any) => sum + (line.debit_amount || 0), 0
                    ) || 0;
                    const totalCredit = entry.booking_entries?.reduce(
                      (sum: number, line: any) => sum + (line.credit_amount || 0), 0
                    ) || 0;

                    return (
                      <tr key={entry.id} className="border-b border-brand-border hover:bg-brand-secondary/50 cursor-pointer">
                        <td className="px-4 py-3 font-mono text-sm">{entry.journal_number}</td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(entry.booking_date).toLocaleDateString('de-CH')}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{entry.reference_number}</td>
                        <td className="px-4 py-3 text-sm">{entry.description}</td>
                        <td className="px-4 py-3 text-right text-sm font-medium">
                          {formatSwissCurrency(totalDebit)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium">
                          {formatSwissCurrency(totalCredit)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={getStatusBadge(entry.status)}>
                            {entry.status}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-brand-border flex items-center justify-between">
              <p className="text-sm text-brand-text-secondary">
                Page {data.currentPage} of {data.totalPages} ({data.totalCount} entries)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-brand-secondary text-brand-primary disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(data.totalPages, p + 1))}
                  disabled={currentPage === data.totalPages}
                  className="px-3 py-1 rounded bg-brand-secondary text-brand-primary disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </AppLayout>
  );
};

export default JournalEntries;
